import { useRef, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileText, Upload, Download, Loader2, Paperclip, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useDispatchDocuments, uploadDispatchDocument, getDispatchDocSignedUrl,
  deleteDispatchDocument, type DispatchDocument, type DispatchDocType,
} from "@/hooks/useDispatchDocuments";


const DOC_TYPE_LABEL: Record<DispatchDocType, string> = {
  bol: "BOL",
  pod: "POD",
  rate_con: "Rate Con",
  photo: "Photo",
  other: "Other",
};

const DOC_TYPE_TONE: Record<DispatchDocType, string> = {
  bol: "bg-sky-500/10 text-sky-700 border-sky-500/30",
  pod: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  rate_con: "bg-brand-teal/15 text-brand-teal border-brand-teal/30",
  photo: "bg-violet-500/10 text-violet-700 border-violet-500/30",
  other: "bg-muted text-muted-foreground border-border",
};

interface Props {
  dispatchId: string | undefined;
  loadId: string;
  uploaderId: string;
  counterpartyUserId: string | null;
  canUpload: boolean;
  /** Optional initial doc_type — defaults to 'bol'. */
  defaultDocType?: DispatchDocType;
  /** Shipper (load owner) user id — used to route notify links correctly. */
  shipperUserId?: string | null;
  /** Optional visual tone classes (fill/border/shadow). Defaults to unchanged styling. */
  cardClassName?: string;
  /** Fired after a confirmed upload/delete so the parent can invalidate its own
   *  caches (POD-dependent milestones, CTA state) immediately. */
  onChanged?: () => void;
}

function fmtSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function DispatchDocumentsPanel({
  dispatchId, loadId, uploaderId, counterpartyUserId, canUpload, defaultDocType, shipperUserId, cardClassName,
  onChanged,
}: Props) {
  const { docs, loading, refresh } = useDispatchDocuments(dispatchId);
  const [docType, setDocType] = useState<DispatchDocType>(defaultDocType ?? "bol");
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<DispatchDocument | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const confirmDelete = async () => {
    if (!pendingDelete || !dispatchId) return;
    const doc = pendingDelete;
    setDeletingId(doc.id);
    try {
      await deleteDispatchDocument({
        documentId: doc.id,
        filePath: doc.file_path,
        dispatchId,
        actorId: uploaderId,
        fileName: doc.file_name,
        docType: doc.doc_type,
      });
      toast.success(`${doc.file_name} deleted`);
      setPendingDelete(null);
    } catch (e: any) {
      toast.error(e?.message || "Delete failed");
    } finally {
      setDeletingId(null);
      refresh();
      onChanged?.();
    }
  };



  const triggerPick = () => fileInputRef.current?.click();

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files[0] || !dispatchId) return;
    setUploading(true);
    try {
      await uploadDispatchDocument({
        dispatchId, loadId, file: files[0], docType, uploaderId, counterpartyUserId, shipperUserId,
      });
      toast.success(`${files[0].name} uploaded`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      // Don't wait on realtime — refresh this list AND the parent's load detail
      // so a POD immediately unlocks the next milestone / CTA.
      await refresh();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const download = async (d: { id: string; file_path: string; file_name: string }) => {
    setDownloadingId(d.id);
    try {
      const url = await getDispatchDocSignedUrl(d.file_path);
      if (!url) throw new Error("Could not generate download link");
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener";
      a.download = d.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e: any) {
      toast.error(e?.message || "Download failed");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card id="dispatch-documents" className={`p-4 scroll-mt-24 ${cardClassName ?? ""}`}>

      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-brand-teal" />
          <h3 className="text-sm font-semibold uppercase tracking-wider">Documents</h3>
          {docs.length > 0 && (
            <span className="text-xs text-muted-foreground">({docs.length})</span>
          )}
        </div>
      </div>

      {canUpload && dispatchId && (
        <div className="mb-3 flex items-center gap-2 flex-wrap">
          <Select value={docType} onValueChange={(v) => setDocType(v as DispatchDocType)}>
            <SelectTrigger className="h-11 w-40 text-xs sm:h-8 sm:w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="bol">BOL</SelectItem>
              <SelectItem value="pod">POD</SelectItem>
              <SelectItem value="rate_con">Rate Con</SelectItem>
              <SelectItem value="photo">Photo</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,.webp,application/pdf,image/*"
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <Button size="sm" variant="outline" className="h-11 gap-1.5 sm:h-8" onClick={triggerPick} disabled={uploading}>
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Uploading…" : "Upload"}
          </Button>
          <span className="text-[11px] text-muted-foreground">PDF, JPG, PNG, HEIC · max 10MB</span>
        </div>
      )}

      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : docs.length === 0 ? (
        <div className="text-center py-6">
          <Paperclip className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-xs text-muted-foreground">
            {canUpload ? "Upload BOL, POD, photos, or other paperwork." : "No documents uploaded yet."}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {docs.map((d) => (
            <li key={d.id} className="py-2 flex items-center gap-3">
              <Badge variant="outline" className={`${DOC_TYPE_TONE[d.doc_type]} text-[10px] shrink-0`}>
                {DOC_TYPE_LABEL[d.doc_type]}
              </Badge>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.file_name}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {d.uploader_name} · {fmtDate(d.created_at)} {d.file_size ? `· ${fmtSize(d.file_size)}` : ""}
                </div>
              </div>
              <Button
                size="sm" variant="ghost" className="h-11 px-3 gap-1 shrink-0 sm:h-7 sm:px-2"
                onClick={() => download(d)}
                disabled={downloadingId === d.id}
              >
                {downloadingId === d.id
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <Download className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="sm" variant="ghost"
                className="h-11 px-3 shrink-0 text-muted-foreground hover:text-destructive sm:h-7 sm:px-2"
                onClick={() => setPendingDelete(d)}
                disabled={deletingId === d.id}
                aria-label={`Delete ${d.file_name}`}
                title="Delete document"
              >
                {deletingId === d.id
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  : <X className="h-3.5 w-3.5" />}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this document?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete?.file_name} will be permanently removed for both the shipper
              and the broker. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!deletingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={!!deletingId}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>

  );
}
